import * as React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  Alert, Button, Input, Modal, ModalBody, ModalFooter, ModalHeader,
  Nav, NavItem, NavLink, Table
} from 'reactstrap';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization, withRouterAndHelpers, RouterProps } from '../../utils/wrappers';
import { userApiBasePath } from '../../redux/selectors/userApi';
import * as selectors from '../../redux/selectors';
import Footer from '../../shared/Footer';
import AdminGuideLink from '../../shared/AdminGuideLink';
import {
  NormalizedNotification,
  normalizeNotification,
  resolveNotificationPath
} from '../../utils/notificationUtils';
import { formatAdminDateTime, competitionTimeZone } from '../../utils/adminDateTime';
import { makeStoryPath } from '../../utils/routerHelper';
import './moderation.scss';

type EngagementTab = 'flags' | 'likes' | 'comments' | 'alerts';

interface FlagCaseContext {
  storyID?: number;
  contentExcerpt?: string;
  contentFull?: string;
  authorDisplayName?: string;
  contentAvailable?: boolean;
  postedAt?: string;
}

interface FlagCase {
  id: number;
  flaggableType: string;
  flaggableID: number;
  flagCount: number;
  status: string;
  reasonSummary: string;
  contentOwnerUserID: number;
  divisionEntityID?: number;
  lastFlaggedAt: string;
  notes?: string;
  reviewedAt?: string;
  context?: FlagCaseContext;
  reporters?: Array<{ userID: number; firstName?: string; lastName?: string; contentMD?: string }>;
}

interface Props {
  apiBase: string;
  token: string;
  competitionName: string;
  isCompetitionAdmin: boolean;
  competitionTimezone?: string;
}

interface LocalState {
  activeTab: EngagementTab;
  cases: FlagCase[];
  engagementEvents: NormalizedNotification[];
  reportersVisible: boolean;
  error: string;
  notice: string;
  statusFilter: string;
  loadingEngagement: boolean;
  detailCase: FlagCase | null;
  detailLoading: boolean;
  lastReviewedCaseID: number | null;
}

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'REVIEWED', label: 'Reviewed' },
  { value: 'REMOVED', label: 'Removed' },
  { value: 'DISMISSED', label: 'Dismissed' },
  { value: '', label: 'All' }
];

class ModerationPage extends React.Component<LProps<RouterProps<{}> & Props>, LocalState> {
  public state: LocalState = {
    activeTab: 'flags',
    cases: [],
    engagementEvents: [],
    reportersVisible: false,
    error: '',
    notice: '',
    statusFilter: 'OPEN',
    loadingEngagement: false,
    detailCase: null,
    detailLoading: false,
    lastReviewedCaseID: null
  };

  public componentDidMount() {
    const tab = this.tabFromQuery();
    this.setState({ activeTab: tab }, () => {
      if (tab === 'flags') {
        this.loadFlags();
      } else {
        this.loadEngagement(tab);
      }
    });
  }

  private tabFromQuery = (): EngagementTab => {
    const params = new URLSearchParams(this.props.history.location.search);
    const tab = params.get('tab') || 'flags';
    if (tab === 'likes' || tab === 'comments' || tab === 'alerts') {
      return tab;
    }
    return 'flags';
  }

  private setTab = (tab: EngagementTab) => {
    const { history } = this.props;
    history.replace(`${history.location.pathname}?tab=${tab}`);
    this.setState({ activeTab: tab, error: '', notice: '', detailCase: null }, () => {
      if (tab === 'flags') {
        this.loadFlags();
      } else {
        this.loadEngagement(tab);
      }
    });
  }

  private authHeaders = () => ({
    'Authorization': this.props.token,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  })

  private formatWhen = (iso: string) =>
    formatAdminDateTime(iso, this.props.competitionTimezone)

  private loadFlags = async () => {
    const { apiBase, competitionName } = this.props;
    const { statusFilter } = this.state;
    const q = statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : '';
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/moderation/flags${q}`,
        { headers: this.authHeaders() }
      );
      if (res.status === 403) {
        this.setState({ error: 'Moderation access required (competition admin or Division Captain).' });
        return;
      }
      if (!res.ok) {
        this.setState({ error: 'Failed to load moderation queue.' });
        return;
      }
      const data = await res.json();
      this.setState({
        cases: data.flagCases || [],
        reportersVisible: Boolean(data.reportersVisible),
        error: ''
      });
    } catch (_err) {
      this.setState({ error: 'Failed to load moderation queue.' });
    }
  }

  private loadEngagement = async (tab: EngagementTab) => {
    const { apiBase, competitionName, isCompetitionAdmin } = this.props;
    if (!isCompetitionAdmin) {
      this.setState({ error: 'Competition admin access required for engagement review.' });
      return;
    }
    const category = tab === 'likes' ? 'likes' : tab === 'comments' ? 'comments' : 'alerts';
    this.setState({ loadingEngagement: true, error: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/moderation/engagement?category=${category}&pageSize=50`,
        { headers: this.authHeaders() }
      );
      if (res.status === 403) {
        this.setState({ loadingEngagement: false, error: 'Competition admin access required.' });
        return;
      }
      if (!res.ok) {
        this.setState({ loadingEngagement: false, error: 'Failed to load engagement events.' });
        return;
      }
      const data = await res.json();
      const engagementEvents = (data.events || []).map((raw: Record<string, unknown>) => normalizeNotification(raw));
      this.setState({ engagementEvents, loadingEngagement: false, error: '' });
    } catch (_err) {
      this.setState({ loadingEngagement: false, error: 'Failed to load engagement events.' });
    }
  }

  private openDetail = async (flagCase: FlagCase) => {
    const { apiBase, competitionName } = this.props;
    this.setState({ detailCase: flagCase, detailLoading: true, error: '' });
    try {
      const res = await fetch(
        `${apiBase}/competitions/${encodeURIComponent(competitionName)}/moderation/flags/${flagCase.id}`,
        { headers: this.authHeaders() }
      );
      if (!res.ok) {
        this.setState({ detailLoading: false, error: 'Failed to load case details.' });
        return;
      }
      const data = await res.json();
      this.setState({ detailCase: data.flagCase || flagCase, detailLoading: false });
    } catch (_err) {
      this.setState({ detailLoading: false, error: 'Failed to load case details.' });
    }
  }

  private closeDetail = () => this.setState({ detailCase: null, detailLoading: false });

  private updateStatus = async (caseID: number, status: string, closeDetail = false) => {
    const { apiBase, competitionName, t } = this.props;
    const res = await fetch(
      `${apiBase}/competitions/${encodeURIComponent(competitionName)}/moderation/flags/${caseID}`,
      {
        method: 'PUT',
        headers: this.authHeaders(),
        body: JSON.stringify({ status })
      }
    );
    if (!res.ok) {
      this.setState({ error: `Failed to update case ${caseID}.` });
      return;
    }
    const statusMatch = STATUS_OPTIONS.find((o) => o.value === status);
    const statusLabel = statusMatch ? statusMatch.label : status;
    this.setState({
      notice: status === 'REVIEWED'
        ? t('Marked Reviewed. Find it under Reviewed or All.')
        : `${t('Case')} ${caseID} ${t('marked')} ${statusLabel}.`,
      lastReviewedCaseID: status === 'REVIEWED' ? caseID : null,
      detailCase: closeDetail ? null : this.state.detailCase
    });
    this.loadFlags();
  }

  private undoReviewed = async () => {
    const { lastReviewedCaseID } = this.state;
    if (!lastReviewedCaseID) {
      return;
    }
    await this.updateStatus(lastReviewedCaseID, 'OPEN');
    this.setState({ lastReviewedCaseID: null, statusFilter: 'OPEN' }, this.loadFlags);
  }

  private storyLinkForCase = (flagCase: FlagCase): string | null => {
    const { t } = this.props;
    const storyID = flagCase.context && flagCase.context.storyID;
    if (storyID) {
      return makeStoryPath(storyID, t);
    }
    if (flagCase.flaggableType === 'story') {
      return makeStoryPath(flagCase.flaggableID, t);
    }
    return null;
  }

  private contentTypeLabel = (type: string) => (type === 'comment' ? 'Comment' : 'Story');

  private authorLabel = (flagCase: FlagCase) => {
    const name = flagCase.context && flagCase.context.authorDisplayName;
    if (name) {
      return name;
    }
    return `User #${flagCase.contentOwnerUserID}`;
  }

  private openEngagementEvent = (event: NormalizedNotification) => {
    const { history, t } = this.props;
    history.push(resolveNotificationPath(event, t));
  }

  private renderDetailModal() {
    const { t, reportersVisible } = this.props;
    const { detailCase, detailLoading } = this.state;
    if (!detailCase) {
      return null;
    }
    const ctx = detailCase.context || {};
    const storyLink = this.storyLinkForCase(detailCase);
    return (
      <Modal isOpen={true} toggle={this.closeDetail} size="lg">
        <ModalHeader toggle={this.closeDetail}>{t('Moderation details')}</ModalHeader>
        <ModalBody>
          {detailLoading && <div className="cc__lighttext">{t('Loading…')}</div>}
          {!detailLoading &&
            <div>
              <dl className="cc__moderation__detail__meta">
                <dt>{t('Type')}</dt>
                <dd>{this.contentTypeLabel(detailCase.flaggableType)}</dd>
                <dt>{t('Author')}</dt>
                <dd>{this.authorLabel(detailCase)}</dd>
                <dt>{t('Status')}</dt>
                <dd>{detailCase.status}</dd>
                <dt>{t('Reports')}</dt>
                <dd>{detailCase.flagCount}</dd>
                <dt>{t('Last reported')}</dt>
                <dd>{this.formatWhen(detailCase.lastFlaggedAt)}</dd>
                {ctx.postedAt &&
                  <React.Fragment>
                    <dt>{t('Originally posted')}</dt>
                    <dd>{this.formatWhen(ctx.postedAt)}</dd>
                  </React.Fragment>
                }
                {detailCase.reasonSummary &&
                  <React.Fragment>
                    <dt>{t('Report summary')}</dt>
                    <dd>{detailCase.reasonSummary}</dd>
                  </React.Fragment>
                }
              </dl>
              <h5>{t('Content')}</h5>
              {ctx.contentAvailable === false &&
                <Alert color="warning">
                  {t('The underlying content is no longer available. The moderation record is preserved, but live text may have been removed.')}
                </Alert>
              }
              <div className="cc__moderation__detail__content">
                {ctx.contentFull || ctx.contentExcerpt || t('No content text available.')}
              </div>
              {reportersVisible && (detailCase.reporters || []).length > 0 &&
                <div style={{ marginBottom: 16 }}>
                  <h5>{t('Reporters')}</h5>
                  <ul>
                    {(detailCase.reporters || []).map((r) =>
                      <li key={r.userID}>
                        {`${r.firstName || ''} ${r.lastName || ''}`.trim() || `User #${r.userID}`}
                        {r.contentMD ? ` — ${r.contentMD}` : ''}
                      </li>
                    )}
                  </ul>
                </div>
              }
              {!reportersVisible &&
                <p className="cc__lighttext">{t('Reporter identity is hidden from Division Captains.')}</p>
              }
              <details style={{ marginTop: 12 }}>
                <summary>{t('Advanced / technical')}</summary>
                <p className="cc__lighttext" style={{ marginTop: 8 }}>
                  {t('Case')} #{detailCase.id} · {detailCase.flaggableType} #{detailCase.flaggableID}
                  {detailCase.contentOwnerUserID ? ` · ${t('Owner user ID')} ${detailCase.contentOwnerUserID}` : ''}
                </p>
              </details>
            </div>
          }
        </ModalBody>
        <ModalFooter>
          {storyLink &&
            <Button color="link" onClick={() => { this.closeDetail(); this.props.history.push(storyLink); }}>
              {t('View story')}
            </Button>
          }
          {detailCase.status === 'OPEN' &&
            <Button color="secondary" onClick={() => this.updateStatus(detailCase.id, 'REVIEWED', true)}>
              {t('Mark Reviewed')}
            </Button>
          }
          {detailCase.status === 'OPEN' &&
            <Button color="danger" onClick={() => this.updateStatus(detailCase.id, 'REMOVED', true)}>
              {t('Remove Content')}
            </Button>
          }
          {detailCase.status === 'OPEN' &&
            <Button color="link" onClick={() => this.updateStatus(detailCase.id, 'DISMISSED', true)}>
              {t('Dismiss Reports')}
            </Button>
          }
          {detailCase.status === 'REVIEWED' &&
            <Button color="link" onClick={() => this.updateStatus(detailCase.id, 'OPEN', true)}>
              {t('Reopen')}
            </Button>
          }
          <Button color="primary" outline={true} onClick={this.closeDetail}>{t('Close')}</Button>
        </ModalFooter>
      </Modal>
    );
  }

  public render() {
    const { t, isCompetitionAdmin } = this.props;
    const {
      activeTab, cases, engagementEvents, reportersVisible, error, notice,
      statusFilter, loadingEngagement, lastReviewedCaseID
    } = this.state;
    return (
      <div>
        <div className="container" style={{ maxWidth: 1100, paddingTop: 24, paddingBottom: 40 }}>
          <h2>{t('Engagement review')}</h2>
          <AdminGuideLink sectionId="moderation" label={t('Moderation & privacy')} t={t} />
          <p className="cc__lighttext">
            {t('Review flagged content with enough context to decide. Items marked Reviewed remain available under Reviewed or All.')}
          </p>
          <Nav tabs={true} style={{ marginBottom: 16 }}>
            <NavItem>
              <NavLink href="#" active={activeTab === 'flags'} onClick={(e) => { e.preventDefault(); this.setTab('flags'); }}>
                {t('Flags')}
              </NavLink>
            </NavItem>
            {isCompetitionAdmin &&
              ['likes', 'comments', 'alerts'].map((tab) =>
                <NavItem key={tab}>
                  <NavLink
                    href="#"
                    active={activeTab === tab}
                    onClick={(e) => { e.preventDefault(); this.setTab(tab as EngagementTab); }}
                  >
                    {t(tab.charAt(0).toUpperCase() + tab.slice(1))}
                  </NavLink>
                </NavItem>
              )
            }
          </Nav>
          {error && <Alert color="danger">{error}</Alert>}
          {notice && <Alert color="success">{notice}</Alert>}
          {lastReviewedCaseID && activeTab === 'flags' &&
            <Alert color="info">
              {t('Marked Reviewed.')}{' '}
              <Button color="link" size="sm" onClick={this.undoReviewed}>{t('Undo')}</Button>
            </Alert>
          }

          {activeTab === 'flags' &&
            <div>
              <div style={{ marginBottom: 12, maxWidth: 220 }}>
                <Input
                  type="select"
                  value={statusFilter}
                  onChange={(e) => this.setState({ statusFilter: e.target.value }, this.loadFlags)}
                >
                  {STATUS_OPTIONS.map((opt) =>
                    <option key={opt.value || 'all'} value={opt.value}>{t(opt.label)}</option>
                  )}
                </Input>
              </div>
              <Table responsive={true} size="sm">
                <thead>
                  <tr>
                    <th>{t('Type')}</th>
                    <th>{t('Author')}</th>
                    <th>{t('Content')}</th>
                    <th>{t('Reports')}</th>
                    <th>{t('When')}</th>
                    <th>{t('Status')}</th>
                    <th>{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cases.map((c) =>
                    <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => this.openDetail(c)}>
                      <td>{this.contentTypeLabel(c.flaggableType)}</td>
                      <td>{this.authorLabel(c)}</td>
                      <td className="cc__moderation__excerpt">
                        {(c.context && c.context.contentExcerpt) || c.reasonSummary || '—'}
                      </td>
                      <td>{c.flagCount}</td>
                      <td>{this.formatWhen(c.lastFlaggedAt)}</td>
                      <td>{c.status}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <Button size="sm" color="primary" outline={true} onClick={() => this.openDetail(c)}>
                          {t('View details')}
                        </Button>
                      </td>
                    </tr>
                  )}
                  {cases.length === 0 &&
                    <tr><td colSpan={7} className="cc__lighttext">{t('No flagged content in this filter.')}</td></tr>
                  }
                </tbody>
              </Table>
              {!reportersVisible &&
                <p className="cc__lighttext">{t('Reporter identity is hidden from Division Captains.')}</p>
              }
            </div>
          }

          {activeTab !== 'flags' &&
            <div>
              {loadingEngagement && <div className="cc__lighttext">{t('Loading…')}</div>}
              {!loadingEngagement &&
                <Table responsive={true} size="sm">
                  <thead>
                    <tr>
                      <th>{t('When')}</th>
                      <th>{t('Title')}</th>
                      <th>{t('Details')}</th>
                      <th>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {engagementEvents.map((event) =>
                      <tr key={event.id}>
                        <td>{this.formatWhen(event.createdAt)}</td>
                        <td>{event.title}</td>
                        <td>{event.body}</td>
                        <td>
                          <Button size="sm" color="link" onClick={() => this.openEngagementEvent(event)}>
                            {event.type === 'story_commented' ? t('View comments') : t('View story')}
                          </Button>
                        </td>
                      </tr>
                    )}
                    {engagementEvents.length === 0 &&
                      <tr><td colSpan={4} className="cc__lighttext">{t('No events in this category yet.')}</td></tr>
                    }
                  </tbody>
                </Table>
              }
            </div>
          }
        </div>
        {this.renderDetailModal()}
        <Footer />
      </div>
    );
  }
}

const mapStateToProps = (state: State): Partial<Props> => ({
  apiBase: userApiBasePath(state),
  token: state.token || '',
  competitionName: (state.competition && state.competition.name) || selectors.competitionName(state) || '',
  isCompetitionAdmin: selectors.isCompetitionAdmin(state),
  competitionTimezone: competitionTimeZone(state.competition)
});

export default asPage({ requiresCompetitionStart: false, authenticated: true })(compose(
  withLocalization('account'),
  withRouterAndHelpers,
  connect(mapStateToProps)
)(ModerationPage));
